/**
 * ThreadMenu (INBOX-PODIUM-FEEL slice 4) — the Podium-style "⋮" overflow on a
 * conversation. Holds the less-common per-thread actions so the header stays
 * clean: open the customer record, mark unread, copy the phone number.
 *
 * Each item does something real. Inline contact-editing and SMS unsubscribe are
 * deliberately NOT here yet — they need new infrastructure (a Pegasus customer-
 * contact override + an SMS suppression list), so they're their own slice rather
 * than a button that doesn't work.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, ExternalLink, MailOpen, Copy, Check, UserCog, BellOff, BellRing } from "lucide-react";
import { cn } from "@/lib/cn";
import { TOOL_BTN_ICON } from "./toolbar";
import { supabase } from "@/lib/supabase";
import { useNavigateWithBack } from "@/lib/navBack";
import { ContactModal } from "@/features/customer-360/CustomerContacts";
import { useUpdateConversation, type InboxConversation } from "./api";

export function ThreadMenu({ conversation }: { conversation: InboxConversation }) {
  const navigate = useNavigateWithBack();
  const update = useUpdateConversation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editContact, setEditContact] = useState(false);
  const [confirmUnsub, setConfirmUnsub] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const ep = conversation.endpoint ?? "";
  const epIsEmail = ep.includes("@");
  const phone10 = epIsEmail ? "" : ep.replace(/\D/g, "").slice(-10);
  const isSms = phone10.length === 10;

  const { data: suppressed } = useQuery({
    queryKey: ["sms_suppressed", phone10],
    enabled: isSms,
    queryFn: async () => {
      const { data } = await supabase.from("sms_suppression").select("phone10").eq("phone10", phone10).maybeSingle();
      return !!data;
    },
  });

  const toggleSuppress = async () => {
    if (!isSms) return;
    if (suppressed) {
      await supabase.from("sms_suppression").delete().eq("phone10", phone10);
    } else {
      const { data: u } = await supabase.auth.getUser();
      await supabase.from("sms_suppression").insert({ phone10, reason: "office", suppressed_by: u.user?.email ?? null });
    }
    qc.invalidateQueries({ queryKey: ["sms_suppressed", phone10] });
    setConfirmUnsub(false);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setConfirmUnsub(false);
  }, [open]);

  const markUnread = () => {
    update.mutate({
      conversation_key: conversation.conversation_key,
      custno: conversation.custno,
      endpoint: conversation.endpoint,
      patch: { last_read_at: null },
    });
    setOpen(false);
  };

  const copyNumber = async () => {
    if (!conversation.endpoint) return;
    try {
      await navigator.clipboard.writeText(conversation.endpoint);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked — no-op */
    }
  };

  const item = "flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-slate-700 hover:bg-slate-50";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="More"
        className={TOOL_BTN_ICON}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1.5 w-56 overflow-hidden rounded-xl border border-hairline bg-white py-1 shadow-e3">
          {conversation.custno && (
            <button
              type="button"
              onClick={() => {
                navigate(`/customers/${conversation.custno}`);
                setOpen(false);
              }}
              className={item}
            >
              <ExternalLink className="h-3.5 w-3.5 text-slate-400" /> Open customer record
            </button>
          )}
          {conversation.custno && (
            <button
              type="button"
              onClick={() => {
                setEditContact(true);
                setOpen(false);
              }}
              className={item}
            >
              <UserCog className="h-3.5 w-3.5 text-slate-400" /> Edit contact info
            </button>
          )}
          <button type="button" onClick={markUnread} className={item}>
            <MailOpen className="h-3.5 w-3.5 text-slate-400" /> Mark unread
          </button>
          {conversation.endpoint && (
            <button type="button" onClick={copyNumber} className={item}>
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5 text-emerald-600" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5 text-slate-400" /> Copy phone number
                </>
              )}
            </button>
          )}
          {isSms && (
            <button
              type="button"
              onClick={() => {
                if (suppressed || confirmUnsub) toggleSuppress();
                else setConfirmUnsub(true);
              }}
              className={cn(item, !suppressed && confirmUnsub && "text-red-600")}
            >
              {suppressed ? (
                <>
                  <BellRing className="h-3.5 w-3.5 text-emerald-500" /> Resubscribe to texts
                </>
              ) : confirmUnsub ? (
                <>
                  <BellOff className="h-3.5 w-3.5 text-red-500" /> Click again — stop texts
                </>
              ) : (
                <>
                  <BellOff className="h-3.5 w-3.5 text-slate-400" /> Unsubscribe from texts
                </>
              )}
            </button>
          )}
        </div>
      )}
      {editContact && conversation.custno && (
        <ContactModal
          custno={conversation.custno}
          locno={null}
          contact={null}
          prefill={{
            name: conversation.provider_contact_name ?? undefined,
            phone: !epIsEmail && ep ? ep : undefined,
            email: epIsEmail ? ep : undefined,
          }}
          onClose={() => setEditContact(false)}
        />
      )}
    </div>
  );
}
